import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Layout, 
  Typography, 
  Button, 
  Spin, 
  message, 
  Modal, 
  Input, 
  Alert
} from 'antd';
import { 
  SaveOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import AgentConfigService from '../services/AgentConfigService';
import AgentInputTabs from './AgentInputTabs';
import DraggableLayoutManagerV2 from './DraggableLayoutManagerV2';
import AgentSider, { SIDER_WIDTH, SIDER_COLLAPSED_WIDTH } from './AgentSider';
import sessionStorageManager from '../services/sessionStorageManager';
import ProgressStatusBar from './ProgressStatusBar';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

/**
 * AgentPageV2 - Component for displaying an agent outside of a project context
 * Now uses the extracted AgentSider component for the sidebar
 */
const AgentPageV2 = ({ username, sessionId, userRole, onLogout }) => {
  const { agentId, projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine source hub based on URL pattern
  const isRCMSource = location.pathname.startsWith('/rcm/');
  const isHUBSource = location.pathname.startsWith('/hub/');
  const sourceHub = isRCMSource ? 'RCM' : 'HUB';
  
  // Hub configuration based on source
  const hubConfig = {
    RCM: {
      label: 'RCM AGENT HUB',
      route: '/rcm',
      icon: '/resources/icons/rcm_inactive_icon.png'
    },
    HUB: {
      label: 'AGENT HUB',
      route: '/agent-hub',
      icon: '/resources/icons/hub_inactive_icon.png'
    }
  };
  
  const currentHubConfig = hubConfig[sourceHub];
  
  // State for UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  
  // State for agent data
  const [agent, setAgent] = useState(null);

  const [savedSessions, setSavedSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [sessionName, setSessionName] = useState('');

  // State for agent execution
  const [apiData, setApiData] = useState(null);
  const [agentLayout, setAgentLayout] = useState(null);
  const [currentWorkflowState, setCurrentWorkflowState] = useState(null);
  const [visibleComponents, setVisibleComponents] = useState({});
  const [activeAgentTab, setActiveAgentTab] = useState('');
  
  // Progress tracking states
  const [isProcessing, setIsProcessing] = useState(false);

  // Help modal state
  const [helpContent, setHelpContent] = useState(null);
  const [helpModalVisible, setHelpModalVisible] = useState(false);
  const [helpLoading, setHelpLoading] = useState(false);

  // Use refs to track initialization status
  const initRef = useRef({
    loadingDone: false,
    agentId: null,
    chatHistoryLoaded: false
  });

  // Ref for screenshot capture (passed to AgentSider)
  const pageRef = useRef(null);

  const handleHomeClick = () => {
    navigate(currentHubConfig.route);
  };

  const handleHelpClick = async () => {
    if (!agentId) return;
    setHelpModalVisible(true);
    if (helpContent !== null) return; // already loaded
    setHelpLoading(true);
    const md = await AgentConfigService.loadAgentHelp(agentId);
    setHelpContent(md);
    setHelpLoading(false);
  };

  // When component mounts, load agent data
  useEffect(() => {
    const loadInitialData = async () => {
      if (initRef.current.agentId === agentId) {
        console.log("Skipping duplicate load for same agentId:", agentId);
        return;
      }
      
      console.log("Loading data for agentId:", agentId);
      initRef.current.agentId = agentId;
      initRef.current.chatHistoryLoaded = false;
      
      try {
        setLoading(true);
        setError(null);
        
        const agentConfig = await loadAgentData();
        await loadSavedSessions(agentConfig);

      } catch (error) {
        console.error("Error in initial data loading:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [agentId, sessionId]);
  

  // useEffect to save state when navigating away
  useEffect(() => {
    return () => {
      if (apiData && currentWorkflowState?.agent_key) {
        const saveState = async () => {
          try {
            console.log('Saving agent state before unmounting...');
            
            const agentApiUrl = await AgentConfigService.getAgentApiUrl(agentId);
            
            const stateToSave = {
              api_data: apiData,
              currentWorkflowState,
              visibleComponents,
              activeAgentTab,
              layout: agentLayout
            };
            
            await fetch(`${agentApiUrl}/save_agent_state`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionId}`
              },
              body: JSON.stringify({
                state: stateToSave,
                agent_key: currentWorkflowState.agent_key,
                project_id: projectId || sessionStorageManager.DEFAULT_PROJECT_ID
              }),
              credentials: 'include'
            });
            
            console.log('Agent state saved successfully');
          } catch (error) {
            console.error('Error saving agent state on unmount:', error);
          }
        };
        
        saveState();
      }
    };
  }, [apiData, currentWorkflowState, visibleComponents, activeAgentTab, agentLayout, agentId, sessionId, projectId]);


  const loadAgentData = async () => {
    try {
      console.log("Loading agent configuration for:", agentId);
      
      const allAgentConfigs = await AgentConfigService.loadAllAgentConfig();
      const agentConfig = allAgentConfigs[agentId];
      
      if (!agentConfig) {
        throw new Error(`Agent with ID ${agentId} not found`);
      }
      
      agentConfig.iconPath = `/resources/icons/${agentConfig.icon || 'default.png'}`;
      setAgent(agentConfig);
      
      const agentKey = sessionStorageManager.getAgentKey(sessionId, agentId);
      
      if (agentKey) {
        console.log("Found saved agent key, loading state:", agentKey);
        const stateLoaded = await loadAgentState(agentKey);
        
        if (stateLoaded) {
          console.log("Agent state loaded successfully");
          setLoading(false);
          return agentConfig;
        }
      }
      
      if (hasInputProcess(agentConfig)) {
        console.log("Agent has input process, waiting for user input");
        try {
          await loadCachedAgentData(agentId);
        } catch (error) {
          console.log("No cached data found, waiting for user input");
        }
      } else {
        console.log("Agent has no input process, auto-triggering");
        await autoTriggerAgentStart(agentConfig);
      }

      return agentConfig;

    } catch (error) {
      console.error("Error loading agent data:", error);
      throw error;
    }
  };

  /////////////////// Progress tracking methods //////////////////

  const startProgressTracking = useCallback((agentKey) => {
    console.log('Starting progress tracking for:', agentKey);
    setIsProcessing(true);
  }, []);

  const stopProgressTracking = useCallback(() => {
    console.log('Stopping progress tracking');
    setIsProcessing(false);
  }, []);

  /////////////////// Progress tracking method ENDS //////////////////


  const loadAgentState = async (agentKey) => {
    try {
      const agentApiUrl = await AgentConfigService.getAgentApiUrl(agentId);
      
      if (!agentApiUrl) {
        throw new Error('Invalid agent API URL');
      }
      
      const projectIdToUse = sessionStorageManager.DEFAULT_PROJECT_ID;
      
      const response = await fetch(`${agentApiUrl}/load_agent_state/${agentKey}?project_id=${projectIdToUse}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.log("No saved state found for this agent key");
        return false;
      }
      
      const result = await response.json();
      
      if (result.success && result.state) {
        const loadedApiData = result.state.api_data;
        const loadedLayout = result.state.layout;
        
        const componentData = {};
        const nonComponentFields = ['agent_key', 'project_id', 'updatedComponentIds'];
        
        if (loadedApiData) {
          Object.keys(loadedApiData).forEach(key => {
            if (!nonComponentFields.includes(key)) {
              componentData[key] = loadedApiData[key];
            }
          });
        }
        
        setApiData(componentData);
        
        if (loadedLayout !== undefined && loadedLayout !== null) {
          setAgentLayout(loadedLayout);
        } else {
          setAgentLayout(2);
        }
        
        setCurrentWorkflowState({
          ...result.state.currentWorkflowState,
          agent_key: agentKey
        });
        
        setActiveAgentTab(result.state.activeAgentTab || '');
        
        if (result.state.visibleComponents && Object.keys(result.state.visibleComponents).length > 0) {
          setVisibleComponents(result.state.visibleComponents);
        } else {
          const defaultVisibility = {};
          Object.keys(componentData).forEach(key => {
            const component = componentData[key];
            if (component && 
                typeof component === 'object' && 
                component.uuid && 
                component.type) {
              defaultVisibility[key] = true;
            }
          });
          setVisibleComponents(defaultVisibility);
        }
        
        message.success(`Restored previous session for ${agentId}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Error loading agent state:", error);
      return false;
    }
  };  


  const hasInputProcess = (agent) => {
    if (!agent) return false;
    
    return agent.input_process && 
          Array.isArray(agent.input_process) && 
          agent.input_process.length > 0 &&
          agent.input_process.some(process => 
            process.input_parameter || process.file_parameter
          );
  };
  
  
  const loadCachedAgentData = async (agentId) => {
    try {
      const agentConfig = await AgentConfigService.loadAllAgentConfig();
      const agentData = agentConfig[agentId];
      
      if (!agentData || !agentData.endpoint) {
        throw new Error('Invalid agent configuration');
      }
      
      const baseUrl = agentData.endpoint.split('/').slice(0, 3).join('/');
      
      const layoutResponse = await fetch(`${baseUrl}/get_agent_layout`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`
        },
        credentials: 'include'
      });
      
      if (!layoutResponse.ok) {
        throw new Error(`Failed to fetch layout: ${layoutResponse.status}`);
      }
      
      const layoutData = await layoutResponse.json();
      setAgentLayout(layoutData);
      
      const lastWorkflowKey = `last_workflow_${agentId}`;
      const lastWorkflowJson = localStorage.getItem(lastWorkflowKey);
      
      if (!lastWorkflowJson) {
        console.log('No previous workflow state found for this agent');
        return;
      }
      
      const workflowState = JSON.parse(lastWorkflowJson);
      setCurrentWorkflowState(workflowState);
      
      const response = await fetch(`${baseUrl}/continue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          [workflowState.primaryKeyField]: workflowState.primaryKeyValue,
          conversation_history: { messages: [] },
          project_id: sessionStorageManager.DEFAULT_PROJECT_ID
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('No cached data found for this workflow');
          localStorage.removeItem(lastWorkflowKey);
        } else {
          throw new Error(`API request failed with status ${response.status}`);
        }
        return;
      }
      
      const data = await response.json();
      
      if (Object.keys(data).length > 0) {
        setApiData(data);
        
        const newVisibility = {};
        Object.keys(data).forEach(key => {
          newVisibility[key] = true;
        });
        setVisibleComponents(newVisibility);
        
        message.success('Cached data loaded successfully');
      }
    } catch (error) {
      console.error("Error loading cached data:", error);
      message.info('No previous data found for this agent');
    }
  };
  
  const autoTriggerAgentStart = async (agentData) => {
    try {
      console.log("Auto-triggering agent start:", agentData.name);
      
      const isValid = await validateSession();
      if (!isValid) return false;
      
      if (!agentData || !agentData.endpoint) {
        throw new Error('Invalid agent configuration - missing endpoint');
      }
      
      const apiUrl = agentData.endpoint.endsWith('/start') ? 
        agentData.endpoint : `${agentData.endpoint}/start`;
      
      const requestBody = {
        conversation_history: { messages: [] },
        project_id: sessionStorageManager.DEFAULT_PROJECT_ID,
        agent_type: agentData.rag_config?.agent_type  
      };
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(requestBody),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.agent_key) {
        setCurrentWorkflowState(prevState => ({
          ...prevState,
          agent_key: data.agent_key
        }));
        
        sessionStorageManager.setAgentKey(sessionId, agentData.id, data.agent_key);
        sessionStorageManager.markActiveAgentSession(sessionId, agentData.id, data.agent_key);
      }
      
      setApiData(data);
      
      const layoutResponse = await fetch(`${agentData.endpoint}/get_agent_layout`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`
        },
        credentials: 'include'
      });
      
      if (!layoutResponse.ok) {
        throw new Error(`Failed to fetch layout: ${layoutResponse.status}`);
      }
      
      const layoutData = await layoutResponse.json();
      setAgentLayout(layoutData);
      
      const newVisibility = {};
      Object.keys(data).forEach(key => {
        newVisibility[key] = true;
      });
      setVisibleComponents(newVisibility);
      
      message.success(`Started ${agentData.name}`);
      return true;
    } catch (error) {
      console.error("Error auto-triggering agent:", error);
      throw error;
    }
  };
  
  const loadSavedSessions = async (agentConfig = null) => {
    const currentAgent = agentConfig || agent;

    if (!currentAgent?.endpoint) {
      console.log("No agent endpoint available for loading sessions");
      return;
    }    

    try {
      setSessionsLoading(true);
      
      const response = await fetch(`${currentAgent.endpoint}/list_sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          agent_type: currentAgent.id || 'unknown_agent',
          project_id: projectId || "11111111-1111-1111-1111-111111111111"
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to load saved sessions');
      }
      
      const sessions = await response.json();
      setSavedSessions(sessions.map(s => ({
        id: s.session_id,
        name: s.session_name,
        date: new Date(s.updated_at).toLocaleDateString() + ' ' + new Date(s.updated_at).toLocaleTimeString(),
        description: s.description,
        component_count: s.component_count
      })));
    } catch (error) {
      console.error("Error loading saved sessions:", error);
      message.error('Failed to load saved sessions');
    } finally {
      setSessionsLoading(false);
    }
  };
  
  const validateSession = async () => {
    try {
      const mainApiUrl = await AgentConfigService.getMainApiUrl();
      const response = await fetch(`${mainApiUrl}/session`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Session invalid or expired');
      }
      
      return true;
    } catch (error) {
      console.error("Session validation error:", error);
      message.error('Your session has expired. Please log in again.');
      onLogout();
      return false;
    }
  };
  
  const getFilteredApiData = () => {
    if (!apiData) return null;
    
    const filtered = {};

    Object.keys(apiData).forEach(key => {
      if (visibleComponents[key]) {
        filtered[key] = apiData[key];
      }
    });
    
    return filtered;
  };



  // Chat history functions
  const getChatHistoryFromStorage = async (agentIdParam) => {
    try {
      const agentApiUrl = await AgentConfigService.getAgentApiUrl(agentIdParam);
      if (!agentApiUrl) {
        console.error('Invalid agent API URL');
        return [];
      }
      
      const agentKey = sessionStorageManager.getAgentKey(sessionId, agentIdParam);
      if (!agentKey) {
        console.log('No agent key found for chat history retrieval');
        return [];
      }
      
      const response = await fetch(`${agentApiUrl}/chat_history/${agentKey}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch chat history: ${response.status}`);
      }
      
      const data = await response.json();
      return data.messages || [];
    } catch (e) {
      console.error('Error retrieving chat history:', e);
      return [];
    }
  };

  const saveChatHistoryToStorage = async (agentIdParam, messages) => {
    try {
      if (!Array.isArray(messages)) {
        console.error('Attempted to save non-array chat history:', messages);
        return;
      }
      
      const agentApiUrl = await AgentConfigService.getAgentApiUrl(agentIdParam);
      if (!agentApiUrl) {
        console.error('Invalid agent API URL');
        return;
      }
      
      const agentKey = sessionStorageManager.getAgentKey(sessionId, agentIdParam);
      if (!agentKey) {
        console.error('No agent key found for chat history storage');
        return;
      }
      
      const response = await fetch(`${agentApiUrl}/save_chat_history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          agent_key: agentKey,
          messages: messages,
          project_id: currentWorkflowState?.projectId || null
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save chat history: ${response.status}`);
      }
      
      console.log(`Saved ${messages.length} messages to server for agent ${agentIdParam}`);
    } catch (e) {
      console.error('Error saving chat history:', e);
    }
  };



  const handleChatMessage = async (messageContent, chatUuid, notifyComponentUpdates, agentKey, projectIdParam) => {
    try {
      if (!messageContent || !messageContent.trim()) {
        return false;
      }
      
      console.log('HANDLE CHAT MESSAGE - PRIOR TO START TRACKING ', agentKey, agent?.id)

      startProgressTracking(agentKey || currentWorkflowState?.agent_key || agent?.id);
      
      const agentApiUrl = await AgentConfigService.getAgentApiUrl(agentId);
      if (!agentApiUrl) {
        console.error('Invalid agent API URL');
        return false;
      }
      
      const chatHistory = await getChatHistoryFromStorage(agentId);
      const userMessage = { role: "user", content: messageContent, type: "text" };
      const updatedHistory = [...chatHistory, userMessage];
      
      await saveChatHistoryToStorage(agentId, updatedHistory);
      
      const payload = {
        ref: {
          policy_id: currentWorkflowState?.primaryKeyValue || "",
          proc_cd: currentWorkflowState?.primaryKeyValue || "",
          agent_id: agent?.id
        },
        last_message: messageContent,
        uuid: chatUuid,
        project_id: projectIdParam || "11111111-1111-1111-1111-111111111111",
        messages: [userMessage],
        agent_type: agent.rag_config?.agent_type
      };
      
      if (agentKey) {
        payload.agent_key = agentKey;
      } else {
        const storedAgentKey = sessionStorageManager.getAgentKey(sessionId, agentId);
        if (storedAgentKey) {
          payload.agent_key = storedAgentKey;
        } else if (currentWorkflowState?.agent_key) {
          payload.agent_key = currentWorkflowState.agent_key;
        }
      }
      
      const response = await fetch(`${agentApiUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error(`Chat API request failed with status ${response.status}`)
        return false;
      }
      
      const data = await response.json();

      if (data.agent_key) {
        setCurrentWorkflowState(prevState => ({
          ...prevState,
          agent_key: data.agent_key
        }));
        
        sessionStorageManager.setAgentKey(sessionId, agentId, data.agent_key);
      }
      
      await saveChatHistoryToStorage(agentId, data["Agent Conversation"].data.messages);      
      setApiData(data);

      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    } finally {
      setTimeout(() => {
        stopProgressTracking();
      }, 500);
    }
  };
  
  useEffect(() => {
    if (!apiData || !apiData["Agent Conversation"] || initRef.current.chatHistoryLoaded) {
      return;
    }
    
    const loadChatHistory = async () => {
      try {
        initRef.current.chatHistoryLoaded = true;
        
        const chatHistory = await getChatHistoryFromStorage(agentId);
        
        if (chatHistory.length > 0) {
          const newData = JSON.parse(JSON.stringify(apiData));
          
          if (newData["Agent Conversation"]) {
            newData["Agent Conversation"] = {
              ...newData["Agent Conversation"],
              data: {
                ...newData["Agent Conversation"].data,
                messages: chatHistory
              }
            };
            
            setApiData(newData);
          }
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
      }
    };
    
    loadChatHistory();
    
  }, [apiData, agentId]);
  
  const handleVersionChange = async (uuid, version) => {
    try {
      setLoading(true);
      
      const agentApiUrl = await AgentConfigService.getAgentApiUrl(agentId);
      
      const response = await fetch(`${agentApiUrl}/get_object_version/${uuid}/${version}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch version: ${response.status}`);
      }
      
      const responseData = await response.json();
      
      const componentTitles = Object.keys(responseData);
      const componentTitle = componentTitles[0];
      
      if (!componentTitle) {
        throw new Error("Invalid response format - no component title found");
      }
      
      setApiData(prevData => {
        const newData = { ...prevData };
        newData[componentTitle] = responseData[componentTitle];
        return newData;
      });
      
      setVisibleComponents(prev => {
        const updated = { ...prev };
        updated[componentTitle] = true;
        return updated;
      });
      
      message.info(`Loaded ${componentTitle} (version ${version})`);
    } catch (err) {
      console.error('Error fetching version:', err);
      setError(err.message);
      message.error(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFormSubmit = async (apiUrl, inputValues, activeTab, primaryKeyInfo, routingParam, hasFiles = false, agent_id = null) => {
    try {
      setLoading(true);
      setError(null);
      setActiveAgentTab(activeTab);
      
      setCurrentWorkflowState({primaryKeyInfo, routingParam: routingParam});
      
      console.log('HANDLEFORMSUBMIT - PRIOR TO START TRACKING ', agent_id, agent.id)

      startProgressTracking(agent_id || agent.id);

      let data = null;
      
      if (hasFiles === true) {
        const formData = new FormData();
        
        Object.entries(inputValues).forEach(([key, value]) => {
          if (value instanceof File) {
            formData.append(key, value, value.name);
          } else if (value !== null && value !== undefined) {
            formData.append(key, String(value));
          }
        });

        formData.append('routing_param', routingParam);
        formData.append('agent_id', agent_id);
        formData.append('project_id', projectId || "11111111-1111-1111-1111-111111111111");
        formData.append('conversation_history', JSON.stringify({ messages: [] }));
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionId}`
          },
          body: formData,
          credentials: 'include'
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API request failed with status ${response.status}: ${errorText}`);
        }
        
        data = await response.json();
        
        if (data && data.agent_key) {
          const updatedWorkflowState = {
            ...primaryKeyInfo,
            agent_key: data.agent_key
          };
          setCurrentWorkflowState(updatedWorkflowState);
          sessionStorageManager.setAgentKey(sessionId, agentId, data.agent_key);
          sessionStorageManager.markActiveAgentSession(sessionId, agent.id, data.agent_key);    
        }
        
        if (data) {
          setApiData(data);
        }
        
      } else {
        const requestBody = {
          ...inputValues,
          routing_param: routingParam,
          conversation_history: { messages: [] },
          agent_id: agent_id,
          project_id: projectId || "11111111-1111-1111-1111-111111111111"
        };
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionId}`
          },
          body: JSON.stringify(requestBody),
          credentials: 'include'
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API request failed with status ${response.status}: ${errorText}`);
        }
        
        data = await response.json();

        if (data && data.agent_key) {
          const updatedWorkflowState = {
            ...primaryKeyInfo,
            agent_key: data.agent_key
          };
          setCurrentWorkflowState(updatedWorkflowState);
          
          sessionStorageManager.setAgentKey(sessionId, agentId, data.agent_key);
        }
        
        if (data) {
          setApiData(data);
        }
      }
      
      const baseAgentApiUrl = await AgentConfigService.getAgentApiUrl(agentId);

      const layoutResponse = await fetch(`${baseAgentApiUrl}/get_agent_layout?routing_param=${routingParam}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`
        },
        credentials: 'include'
      });
      
      if (!layoutResponse.ok) {
        console.warn(`Failed to fetch layout: ${layoutResponse.status}`);
      } else {
        const layoutData = await layoutResponse.json();
        if (layoutData) {
          setAgentLayout(layoutData);
        }
      }

      const newVisibility = {};
      if (data) {
        Object.keys(data).forEach(key => {
          newVisibility[key] = true;
        });
      }
      setVisibleComponents(newVisibility);
      
      if (data) {
        message.success(`Started ${agent.name}`);
      } else {
        message.warning(`Started ${agent.name} but no response data received`);
      }
    } catch (err) {
      console.error('Error in handleFormSubmit:', err);
      setError(err.message);
      message.error(`Error: ${err.message}`);
      stopProgressTracking();
    } finally {
      setLoading(false);
      setTimeout(() => {
        stopProgressTracking();
      }, 500);
    }
  };
  


  const handleComponentUpdate = (result) => {
    if (!result) {
      console.warn('Empty result received in handleComponentUpdate');
      return;
    }
    
    console.log('Component update result received in AgentPageV2:', result);
    
    setApiData(prevData => {
      const newData = { ...prevData };
      
      for (const [key, component] of Object.entries(result)) {
        if (key !== 'updatedComponentIds' &&
            key !== 'evaluationResult' &&
            key !== 'message' &&
            key !== 'agent_key' &&
            key !== 'component_map') {
          newData[key] = component;
        }
      }
      
      return newData;
    });
    
    setVisibleComponents(prev => {
      const updated = { ...prev };
      
      for (const [key, component] of Object.entries(result)) {
        if (key !== 'updatedComponentIds' &&
            key !== 'evaluationResult' &&
            key !== 'message' &&
            key !== 'agent_key' &&
            key !== 'component_map' &&
            component &&
            typeof component === 'object' &&
            component.uuid &&
            component.type) {
          updated[key] = true;
        }
      }
      
      return updated;
    });
  };


  const handleSavedSessionClick = async (clickedSessionId) => {
    try {
      setLoading(true);
      
      const response = await fetch(`${agent.endpoint}/restore_session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          saved_session_id: clickedSessionId,
          project_id: projectId || "11111111-1111-1111-1111-111111111111"
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to restore session');
      }
      
      const result = await response.json();
      
      if (result.success) {
        message.success(`Session restored: ${result.message}`);
        
        const agentType = result.message2 || agent.id;
        
        try {
          const agentApiUrl = await AgentConfigService.getAgentApiUrl(agentId);
          const stateResponse = await fetch(`${agentApiUrl}/load_agent_state/${agentType}?project_id=${projectId || "11111111-1111-1111-1111-111111111111"}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${sessionId}` },
            credentials: 'include'
          });
          

          if (stateResponse.ok) {
            const stateResult = await stateResponse.json();
            
            if (stateResult.success && stateResult.state) {
              const loadedApiData = stateResult.state.api_data;
              const loadedLayout = stateResult.state.layout;
              
              const componentData = {};
              const nonComponentFields = ['agent_key', 'project_id', 'updatedComponentIds'];
              
              if (loadedApiData) {
                Object.keys(loadedApiData).forEach(key => {
                  if (!nonComponentFields.includes(key)) {
                    componentData[key] = loadedApiData[key];
                  }
                });
              }
              
              setApiData(componentData);
              
              if (loadedLayout !== undefined && loadedLayout !== null) {
                setAgentLayout(loadedLayout);
              } else {
                setAgentLayout(2);
              }
              
              setCurrentWorkflowState({
                ...stateResult.state.currentWorkflowState,
                agent_key: agentType
              });
              
              setActiveAgentTab(stateResult.state.activeAgentTab || '');
              
              if (stateResult.state.visibleComponents && Object.keys(stateResult.state.visibleComponents).length > 0) {
                setVisibleComponents(stateResult.state.visibleComponents);
              } else {
                const defaultVisibility = {};
                Object.keys(componentData).forEach(key => {
                  const component = componentData[key];
                  if (component && 
                      typeof component === 'object' && 
                      component.uuid && 
                      component.type) {
                    defaultVisibility[key] = true;
                  }
                });
                setVisibleComponents(defaultVisibility);
              }
              
              sessionStorageManager.setAgentKey(sessionId, agentId, agentType);
            }
          } else {
            console.error("Failed to load agent state after restore");
          }
        } catch (error) {
          console.error("Error refreshing agent state:", error);
        }
      } else {
        message.error(result.error || 'Failed to restore session');
      }
    } catch (error) {
      console.error("Error restoring session:", error);
      message.error('Failed to restore session. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  


  const handleSaveSession = () => {
    if (!apiData || !agent) {
      message.warning('No data to save');
      return;
    }
    
    setSaveModalVisible(true);
  };

  const handleSaveConfirm = async () => {
    if (!sessionName.trim()) {
      message.warning('Please enter a session name');
      return;
    }
    
    try {
      setSaveLoading(true);
      
      const response = await fetch(`${agent.endpoint}/save_session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          agent_type: agent.id || 'unknown_agent',
          project_id: projectId || "11111111-1111-1111-1111-111111111111",
          session_name: sessionName.trim(),
          description: `Saved on ${new Date().toLocaleString()}`,
          overwrite: false
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to save session');
      }
      
      const result = await response.json();
      
      if (result.requires_confirmation) {
        const confirmed = await new Promise(resolve => {
          Modal.confirm({
            title: 'Session Already Exists',
            content: `A session named "${sessionName}" already exists. Do you want to overwrite it?`,
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        
        if (confirmed) {
          return handleSaveWithOverwrite();
        }
        return;
      }
      
      if (result.success) {
        message.success(`Session "${sessionName}" saved successfully`);
        setSaveModalVisible(false);
        setSessionName('');
        loadSavedSessions();
      } else {
        message.error(result.error || 'Failed to save session');
      }
    } catch (error) {
      console.error("Error saving session:", error);
      message.error('Failed to save session. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSaveWithOverwrite = async () => {
    try {
      const response = await fetch(`${agent.endpoint}/save_session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify({
          agent_type: agent.id || 'unknown_agent',
          project_id: projectId || "11111111-1111-1111-1111-111111111111",
          session_name: sessionName.trim(),
          description: `Overwritten on ${new Date().toLocaleString()}`,
          overwrite: true
        }),
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success) {
        message.success(`Session "${sessionName}" updated successfully`);
        setSaveModalVisible(false);
        setSessionName('');
        loadSavedSessions();
      } else {
        message.error(result.error || 'Failed to update session');
      }
    } catch (error) {
      console.error("Error updating session:", error);
      message.error('Failed to update session. Please try again.');
    }
  };


  if (error && !agent) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#f5222d' }}>
        <div style={{ fontSize: '18px', marginBottom: '8px' }}>Error</div>
        <div>{error}</div>
        <Button 
          type="primary" 
          style={{ marginTop: '16px', background: '#6200EE' }}
          onClick={handleHomeClick}
        >
          Return to Home
        </Button>
      </div>
    );
  }
  
  // Calculate content margin based on sider state
  const contentMarginLeft = siderCollapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH;

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }} ref={pageRef}>
      {/* Use the extracted AgentSider component */}
      <AgentSider
        username={username}
        sessionId={sessionId}
        agentId={agentId}
        onLogout={onLogout}
        collapsed={siderCollapsed}
        onCollapsedChange={setSiderCollapsed}
        savedSessions={savedSessions}
        sessionsLoading={sessionsLoading}
        onSessionClick={handleSavedSessionClick}
        onLoadSessions={() => loadSavedSessions()}
        apiData={apiData}
        currentWorkflowState={currentWorkflowState}
        screenshotRef={pageRef}
      />
      
      <Layout style={{ 
        marginLeft: `${contentMarginLeft}px`,
        width: `calc(100% - ${contentMarginLeft}px)`,
        padding: 0,
        backgroundColor: '#f0f2f5'
      }}>
        <Header style={{ 
          background: '#6200EE', 
          padding: '0 16px',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          overflow: 'hidden',
          margin: 0,
          borderBottom: 'none'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            color: 'white',
            overflow: 'hidden'
          }}>
            <span 
              onClick={handleHomeClick} 
              style={{ 
                color: 'white', 
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              <img 
                src={currentHubConfig.icon}
                alt={currentHubConfig.label}
                style={{ width: '20px', height: '20px', opacity: 0.9 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />&nbsp;&nbsp;
              {currentHubConfig.label}
            </span>
            <span style={{ 
              color: 'white', 
              margin: '0 12px',
              fontSize: '24px'
            }}>/</span>
            <span style={{ 
              color: 'white', 
              fontSize: '24px',
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {agent?.name || 'AGENT'}
            </span>
            {agent && (
              <InfoCircleOutlined
                onClick={handleHelpClick}
                style={{
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '16px',
                  marginLeft: '10px',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'color 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'white'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.75)'}
              />
            )}
          </div>
          
          <Button 
            icon={<SaveOutlined />}
            onClick={handleSaveSession}
            loading={saveLoading}
            style={{
              backgroundColor: 'white',
              color: '#6200EE'
            }}
          >
            Save Session
          </Button>
        </Header>
        
        {agent && agent.input_process && Array.isArray(agent.input_process) && agent.input_process.length > 0 && (
          <div style={{
            padding: 0, 
            margin: 0,
            backgroundColor: '#6200EE',
            width: '100%'
          }}>
            <AgentInputTabs 
              activeAgent={agent} 
              onSubmit={handleFormSubmit}
              previousState={currentWorkflowState}
              onTabChange={setActiveAgentTab} 
              sessionId={sessionId} 
            />
          </div>
        )}
        
        <Content style={{ 
          padding: '16px', 
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          margin: 0,
          paddingBottom: '70px'
        }}>
          {loading && !apiData && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                Loading agent...
              </div>
            </div>
          )}

          {error && !loading && (
            <div style={{ padding: '40px', maxWidth: '600px', margin: '0 auto' }}>
              <Alert
                message={error.includes('not found') ? 'Encounter Not Found' : 'Error Loading Encounter'}
                description={(() => {
                  try {
                    const jsonMatch = error.match(/\{.*\}/);
                    if (jsonMatch) {
                      const parsed = JSON.parse(jsonMatch[0]);
                      return parsed.detail || parsed.message || error;
                    }
                  } catch (e) {
                  }
                  return error;
                })()}
                type={error.includes('not found') ? 'warning' : 'error'}
                showIcon
                closable
                onClose={() => setError(null)}
              />
            </div>
          )}
                  
          {!loading && !error && apiData && agentLayout && (
            <DraggableLayoutManagerV2 
              apiData={getFilteredApiData()}
              layout={agentLayout}
              onVersionChange={handleVersionChange}
              onChatMessage={handleChatMessage}
              sessionId={sessionId}
              activeAgent={agent}
              currentWorkflowState={currentWorkflowState}
              username={username}
              projectId={projectId || "11111111-1111-1111-1111-111111111111"}
              onComponentUpdate={handleComponentUpdate}
              onProcessingStart={() => {
                  startProgressTracking(currentWorkflowState?.agent_key || agent?.id);
              }}
              onProcessingEnd={() => {
                  setTimeout(() => {
                    stopProgressTracking();
                  }, 500);
              }}
              agentId={agentId}
            />
          )}
        </Content>
      </Layout>

      {isProcessing && (
        <ProgressStatusBar 
          isProcessing={isProcessing}
          agentName={agent?.name}
          siderCollapsed={siderCollapsed}
          sessionId={sessionId}
          agentId={agentId}
        />
      )}
      
      <Modal
        title="Save Session"
        visible={saveModalVisible}
        onOk={handleSaveConfirm}
        onCancel={() => {
          setSaveModalVisible(false);
          setSessionName('');
        }}
        confirmLoading={saveLoading}
        okText="Save"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: '16px' }}>
          <Text>Enter a name for this session:</Text>
        </div>
        <Input
          placeholder="Session name"
          value={sessionName}
          onChange={(e) => setSessionName(e.target.value)}
          onPressEnter={handleSaveConfirm}
          maxLength={100}
        />
      </Modal>

      <Modal
        title={
          <span style={{ color: '#6200EE', fontWeight: 'bold' }}>
            <InfoCircleOutlined style={{ marginRight: '8px' }} />
            {agent?.name || 'Agent'} — Help
          </span>
        }
        open={helpModalVisible}
        onCancel={() => setHelpModalVisible(false)}
        footer={null}
        width={1200}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto', padding: '16px 24px' } }}
      >
        {helpLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin />
            <div style={{ marginTop: '12px', color: '#888' }}>Loading help content...</div>
          </div>
        ) : helpContent ? (
          <div className="markdown-content">
            <ReactMarkdown>{helpContent}</ReactMarkdown>
          </div>
        ) : (
          <div style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
            No help documentation is available for this agent yet.
          </div>
        )}
      </Modal>
    </Layout>
  );
  
};

export default AgentPageV2;